/**
 * DataBest — Generic CRUD page.
 * Renders a table with list/create/edit/delete for any master-data resource.
 * Kept intentionally simple; each page just declares its columns + fields.
 */
import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import DatePickerUS from "@/components/DatePickerUS";

const emptyState = { title: "", subtitle: "" };

const CrudPage = ({
    title,
    subtitle = "",
    api,
    columns,
    fields,
    idField = "id",
    listParams = null,
    testidPrefix,
    canCreate = true,
    canEdit = true,
    canDelete = true,
    empty = emptyState,
    formComponent: FormComponent = null,
}) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});

    const refresh = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await api.list(listParams);
            setRows(data);
        } catch (err) {
            setError(err?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCreate = () => {
        const initial = {};
        if (fields) {
            for (const f of fields) initial[f.name] = f.default ?? "";
        }
        setForm(initial);
        setEditing({ mode: "create" });
    };

    const openEdit = (row) => {
        const initial = fields
            ? Object.fromEntries(fields.map((f) => [f.name, row[f.name] ?? ""]))
            : { ...row };
        setForm(initial);
        setEditing({ mode: "edit", id: row[idField] });
    };

    const closeModal = () => {
        setEditing(null);
        setForm({});
    };

    const handleSave = async (e, customPayload = null) => {
        if (e && e.preventDefault) e.preventDefault();
        setSaving(true);
        try {
            let clean;
            if (customPayload !== null) {
                clean = customPayload;
            } else {
                clean = {};
                for (const f of fields) {
                    let v = form[f.name];
                    if (v === "" || v === undefined) v = null;
                    if (f.type === "number" && v !== null && v !== "") v = Number(v);
                    if (f.type === "boolean") v = !!form[f.name];
                    clean[f.name] = v;
                }
            }
            if (editing.mode === "create") {
                await api.create(clean);
                toast.success(`${title.slice(0, -1)} created`);
            } else {
                await api.update(editing.id, clean);
                toast.success(`${title.slice(0, -1)} updated`);
            }
            closeModal();
            refresh();
        } catch (err) {
            toast.error(err?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete "${row.name || row.email || row.id}"?`))
            return;
        try {
            await api.remove(row[idField]);
            toast.success("Deleted");
            refresh();
        } catch (err) {
            toast.error(err?.message || "Delete failed");
        }
    };

    const filtered = search
        ? rows.filter((r) =>
              Object.values(r)
                  .filter((v) => typeof v === "string")
                  .some((v) => v.toLowerCase().includes(search.toLowerCase())),
          )
        : rows;

    return (
        <div data-testid={`${testidPrefix}-page`}>
            <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-brand-blue-950 md:text-3xl">
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid={`${testidPrefix}-search`}
                            className="h-10 w-52 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-brand-blue-500 focus:ring-2 focus:ring-brand-blue-500/20"
                        />
                    </div>
                    {canCreate && (
                        <button
                            onClick={openCreate}
                            data-testid={`${testidPrefix}-create-button`}
                            className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-blue-700 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-blue-800"
                        >
                            <Plus className="h-4 w-4" /> New
                        </button>
                    )}
                </div>
            </header>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                {loading ? (
                    <div className="flex items-center justify-center gap-2 p-12 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : error ? (
                    <div className="p-6 text-center text-sm text-red-700">
                        {error}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-slate-500">
                        <p className="font-semibold text-slate-700">
                            {empty.title || "No records yet"}
                        </p>
                        {empty.subtitle && (
                            <p className="mt-1 text-sm">{empty.subtitle}</p>
                        )}
                    </div>
                ) : (
                    <table
                        className="min-w-full divide-y divide-slate-100"
                        data-testid={`${testidPrefix}-table`}
                    >
                        <thead className="bg-slate-50">
                            <tr>
                                {columns.map((c) => (
                                    <th
                                        key={c.key}
                                        className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                                    >
                                        {c.label}
                                    </th>
                                ))}
                                <th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((row) => (
                                <tr
                                    key={row[idField]}
                                    data-testid={`${testidPrefix}-row`}
                                    className="hover:bg-slate-50"
                                >
                                    {columns.map((c) => (
                                        <td
                                            key={c.key}
                                            className="px-4 py-3 text-sm text-slate-700"
                                        >
                                            {c.render
                                                ? c.render(row)
                                                : String(row[c.key] ?? "—")}
                                        </td>
                                    ))}
                                    <td className="px-4 py-3 text-right">
                                        <div className="inline-flex items-center gap-1">
                                            {canEdit && (
                                                <button
                                                    onClick={() =>
                                                        openEdit(row)
                                                    }
                                                    data-testid={`${testidPrefix}-edit-button`}
                                                    className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-brand-blue-700"
                                                    aria-label="Edit"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() =>
                                                        handleDelete(row)
                                                    }
                                                    data-testid={`${testidPrefix}-delete-button`}
                                                    className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600"
                                                    aria-label="Delete"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal */}
            {editing && (
                <div
                    className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4"
                    role="dialog"
                    aria-modal="true"
                    data-testid={`${testidPrefix}-modal`}
                >
                    <div className="flex w-full max-w-lg max-h-[90dvh] flex-col rounded-2xl bg-white shadow-2xl">
                        <div className="shrink-0 flex items-center justify-between border-b border-slate-100 px-5 py-4">
                            <h2 className="font-heading text-lg font-bold text-brand-blue-950">
                                {editing.mode === "create"
                                    ? `New ${title.slice(0, -1)}`
                                    : `Edit ${title.slice(0, -1)}`}
                            </h2>
                            <button
                                onClick={closeModal}
                                aria-label="Close"
                                className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        {FormComponent ? (
                            <FormComponent
                                mode={editing.mode}
                                initialValues={form}
                                saving={saving}
                                onSave={(payload) => handleSave(null, payload)}
                                onClose={closeModal}
                                testidPrefix={testidPrefix}
                            />
                        ) : (
                        <form
                            onSubmit={handleSave}
                            className="flex flex-1 flex-col overflow-hidden"
                            data-testid={`${testidPrefix}-form`}
                        >
                            <div className="flex-1 space-y-4 overflow-y-auto p-5">
                            {fields.map((f) => (
                                <div key={f.name}>
                                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        {f.label}
                                        {f.required && (
                                            <span className="text-red-500">
                                                {" "}
                                                *
                                            </span>
                                        )}
                                    </label>
                                    {f.type === "select" ? (
                                        <select
                                            required={f.required}
                                            value={form[f.name] ?? ""}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    [f.name]: e.target.value,
                                                })
                                            }
                                            data-testid={`${testidPrefix}-field-${f.name}`}
                                            className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20"
                                        >
                                            <option value="">Select…</option>
                                            {(f.options || []).map((o) => (
                                                <option
                                                    key={o.value}
                                                    value={o.value}
                                                >
                                                    {o.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : f.type === "boolean" ? (
                                        <label className="inline-flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={!!form[f.name]}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        [f.name]:
                                                            e.target.checked,
                                                    })
                                                }
                                                data-testid={`${testidPrefix}-field-${f.name}`}
                                                className="h-4 w-4 rounded border-slate-300 text-brand-blue-700 focus:ring-brand-blue-500"
                                            />
                                            Active
                                        </label>
                                    ) : f.type === "textarea" ? (
                                        <textarea
                                            required={f.required}
                                            value={form[f.name] ?? ""}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    [f.name]: e.target.value,
                                                })
                                            }
                                            data-testid={`${testidPrefix}-field-${f.name}`}
                                            className="min-h-[90px] w-full rounded-lg border border-slate-200 bg-white p-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20"
                                        />
                                    ) : f.type === "date" ? (
                                        <DatePickerUS
                                            value={form[f.name] ?? ""}
                                            onChange={(iso) =>
                                                setForm({
                                                    ...form,
                                                    [f.name]: iso,
                                                })
                                            }
                                            testId={`${testidPrefix}-field-${f.name}`}
                                            className="h-11 w-full"
                                            clearable={!f.required}
                                        />
                                    ) : (
                                        <input
                                            type={f.type || "text"}
                                            required={f.required}
                                            value={form[f.name] ?? ""}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    [f.name]: e.target.value,
                                                })
                                            }
                                            data-testid={`${testidPrefix}-field-${f.name}`}
                                            className="h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-brand-blue-500 focus:outline-none focus:ring-2 focus:ring-brand-blue-500/20"
                                        />
                                    )}
                                    {f.hint && (
                                        <p className="mt-1 text-xs text-slate-500">
                                            {f.hint}
                                        </p>
                                    )}
                                </div>
                            ))}
                            </div>
                            <div className="shrink-0 flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="h-10 rounded-lg px-4 text-sm font-medium text-slate-600 hover:bg-slate-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    data-testid={`${testidPrefix}-save-button`}
                                    className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-blue-700 px-5 text-sm font-semibold text-white hover:bg-brand-blue-800 disabled:opacity-70"
                                >
                                    {saving && (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    )}
                                    Save
                                </button>
                            </div>
                        </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CrudPage;
